import { useCallback, useMemo } from 'react';

/**
 * Hook for creating stable, memoized handlers to prevent unnecessary re-renders.
 *
 * Provides memoized versions of commonly used handlers with optimal dependency arrays
 * to minimize re-creation and improve component performance.
 *
 * @param handlers - Object containing the handler functions to stabilize
 * @returns Memoized handlers object with stable references
 */

interface HandlerInput {
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, setFieldValue: (field: string, value: any) => void, fieldName: string) => void;
    getMeasurementUnit: (unit: string[] | undefined) => string;
    handleAccordionToggle: (groupName: string) => void;
    handleEsalModalClose: () => void;
    handleEsalCalculationComplete: (result: number | { esalFinalValue: number; totalPassagesPerYear: number }) => void;
}

interface MemoizedHandlers {
    handleInputChange: HandlerInput['handleInputChange'];
    getMeasurementUnit: HandlerInput['getMeasurementUnit'];
    handleAccordionToggle: HandlerInput['handleAccordionToggle'];
    handleEsalModalClose: HandlerInput['handleEsalModalClose'];
    handleEsalCalculationComplete: HandlerInput['handleEsalCalculationComplete'];
}

export const useMemoizedHandlers = (handlers: HandlerInput): MemoizedHandlers => {
    // Memoize handleInputChange - no dependencies needed as we want latest handler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedHandleInputChange = useCallback(handlers.handleInputChange, []);

    // Memoize getMeasurementUnit - no dependencies needed as we want latest handler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedGetMeasurementUnit = useCallback(handlers.getMeasurementUnit, []);

    // Memoize accordion toggle handler - no dependencies needed as we want latest handler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedHandleAccordionToggle = useCallback(handlers.handleAccordionToggle, []);

    // Memoize ESAL modal handlers - no dependencies needed as we want latest handler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedHandleEsalModalClose = useCallback(handlers.handleEsalModalClose, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedHandleEsalCalculationComplete = useCallback(handlers.handleEsalCalculationComplete, []);

    // Return stable object reference
    return useMemo(
        () => ({
            handleInputChange: memoizedHandleInputChange,
            getMeasurementUnit: memoizedGetMeasurementUnit,
            handleAccordionToggle: memoizedHandleAccordionToggle,
            handleEsalModalClose: memoizedHandleEsalModalClose,
            handleEsalCalculationComplete: memoizedHandleEsalCalculationComplete,
        }),
        [memoizedHandleInputChange, memoizedGetMeasurementUnit, memoizedHandleAccordionToggle, memoizedHandleEsalModalClose, memoizedHandleEsalCalculationComplete]
    );
};
